import { useEffect, useRef, useState } from "react";
import { DisabledPrompt } from "@bcm-dev/bcms-vite";
import CustomSelect, {
  CustomSelectItem,
} from "components/custom-ui/CustomSelect";
import {
  ContentHeaderView,
  FormContainer,
  FormRowView,
  WidgetContainer,
} from "components/shared";
import { PrimaryButton } from "components/ui";
import { Profile } from "model";

import "./mobile.css";
import "./styles.css";

export default function ProfilePicker(props) {
  const {
    ui,
    onChange,
    onSelectClick,
    profiles = [],
    title = "Change Current Profile",
    disableBusiness,
    disableConsumer,
  } = props;

  const disabledPromptButtonRef = useRef(null);
  const defaultProfileWasSetRef = useRef(false);

  const [selectResetKey, setSelectResetKey] = useState(0);

  const profileID =
    props.profileID ?? Profile.getDefaultBrandedProfile().id;

  const businessDisabled =
    disableBusiness === true || disableBusiness === "true";

  const consumerDisabled =
    disableConsumer === true || disableConsumer === "true";

  const isProfileDisabled = (profile) => {
    if (!profile) {
      return false;
    }

    const isBusiness = profile.type === "business";

    return (
      (businessDisabled && isBusiness) ||
      (consumerDisabled && !isBusiness)
    );
  };

  const currentProfile = profiles.find(
    (profile) => String(profile.id) === String(profileID)
  );

  const firstEnabledProfile = profiles.find(
    (profile) => !isProfileDisabled(profile)
  );

  const currentProfileIsDisabled =
    currentProfile && isProfileDisabled(currentProfile);

  const activeProfileID =
    currentProfile && !currentProfileIsDisabled
      ? currentProfile.id
      : firstEnabledProfile?.id ?? profileID;

  useEffect(() => {
    if (defaultProfileWasSetRef.current) {
      return;
    }

    if (!currentProfileIsDisabled || !firstEnabledProfile) {
      return;
    }

    defaultProfileWasSetRef.current = true;

    onChange?.({
      target: {
        value: firstEnabledProfile.id,
      },
    });
  }, [
    currentProfileIsDisabled,
    firstEnabledProfile?.id,
    onChange,
  ]);

  const openDisabledPrompt = () => {
    disabledPromptButtonRef.current?.click();
  };

  const stopEvent = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    event?.nativeEvent?.stopImmediatePropagation?.();
  };

  const handleProfileChange = (eventOrValue) => {
    const selectedProfileID =
      eventOrValue?.target?.value ??
      eventOrValue?.value ??
      eventOrValue;

    const nextProfile = profiles.find(
      (profile) =>
        String(profile.id) === String(selectedProfileID)
    );

    if (isProfileDisabled(nextProfile)) {
      stopEvent(eventOrValue);
      openDisabledPrompt();

      // Reset the select to activeProfileID.
      setSelectResetKey((currentKey) => currentKey + 1);

      return;
    }

    onChange?.(eventOrValue);
  };

  const handleSelectClick = (event) => {
    const selectedProfile = profiles.find(
      (profile) =>
        String(profile.id) === String(activeProfileID)
    );

    if (isProfileDisabled(selectedProfile)) {
      stopEvent(event);
      openDisabledPrompt();
      return;
    }

    onSelectClick?.(event);
  };

  const items = profiles.map((profile) => (
    <CustomSelectItem
      key={profile.id}
      value={profile.id}
      dataPoint={`Current_Profile_Select_Item_${profile.id}`}
    >
      {profile.name}
    </CustomSelectItem>
  ));

  return (
    <WidgetContainer className="change-profile-widget">
      <ContentHeaderView
        text={title}
        className="show-on-desktop"
      />

      <FormContainer className="small-form-width">
        <FormRowView
          className="profile-select-form-row"
          text="Current Profile"
        >
          <CustomSelect
            key={`profile-select-${selectResetKey}`}
            active={ui.currentProfileSelectIsActive}
            value={activeProfileID}
            onChange={handleProfileChange}
            dataPoint="Current_Profile_Select"
          >
            {items}
          </CustomSelect>
        </FormRowView>

        <PrimaryButton
          className="change-profile-button"
          text="Select"
          dataPoint="Select_Button"
          onClick={handleSelectClick}
        />

        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            width: "1px",
            height: "1px",
            overflow: "hidden",
            opacity: 0,
            pointerEvents: "none",
          }}
        >
          <DisabledPrompt>
            <button
              ref={disabledPromptButtonRef}
              type="button"
              tabIndex={-1}
            >
              Disabled profile prompt
            </button>
          </DisabledPrompt>
        </div>
      </FormContainer>
    </WidgetContainer>
  );
}